import React from 'react';
import styled, { css } from 'styled-components';
import { borders, colors, typography } from '@99designs/ui/settings';
import { Stack } from '@99designs/ui';
import Label from './Label';

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    error?: string;
    label: string;
}

export const Error = styled.span`
    color: ${colors.semantic.error};
`;

export const Input = styled.input<{ error?: React.ReactNode }>`
    padding: 6px 12px;
    display: block;
    color: ${colors.puntRoad};
    border: ${borders.textInput};
    border-radius: ${borders.borderRadiuses.medium}px;
    background-color: ${colors.bleachedAs};
    font-size: ${typography.typeScale.size3}px;
    width: 100%;
    max-width: ${typography.maxWidths.form.default};
    line-height: 1.8;

    &:focus {
        border-color: ${colors.fiftyFirstShade};
        outline: none;
    }

    &::placeholder {
        color: ${colors.fiftyFirstShade};
    }

    ${({ error }) =>
        error &&
        css`
            border-color: ${colors.semantic.error};
        `}
`;

const TextField = React.forwardRef(function TextField(
    {
        error,
        label,
        id,
        name,
        type,
        autoFocus = false,
        autoComplete,
        ...fieldProps
    }: TextFieldProps,
    ref: React.RefObject<HTMLInputElement>
) {
    return (
        <Stack space="space7">
            <Label htmlFor={id}>{label}</Label>
            {!!error && (
                <Error role="alert" id={`${name}-error`}>
                    {error}
                </Error>
            )}
            <Input
                error={error}
                aria-invalid={!!error}
                aria-describedby={`${name}-error`}
                id={id}
                name={name}
                ref={ref}
                type={type}
                autoFocus={autoFocus}
                autoComplete={autoComplete}
                {...fieldProps}
            />
        </Stack>
    );
});

export default TextField;
